import { parsePhoneNumberFromString, CountryCode } from "libphonenumber-js";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { JobStatus } from "./constants";

dayjs.extend(relativeTime);
/**
 * Extracts the dial code (country calling code) from a phone number.
 *
 * @param phoneNumber - The phone number to extract the dial code from.
 * @param countryCode - The country code (optional) to help parse the number.
 * @returns The extracted dial code, or null if the phone number is invalid.
 */
const extractDialCode = (phoneNumber, countryCode = "") => {
  const parsedPhoneNumber = parsePhoneNumberFromString(
    phoneNumber,
    countryCode
  );

  const dialCode = `+${parsedPhoneNumber?.countryCallingCode}`; // Get the dial code with '+'
  const localNumber = parsedPhoneNumber?.nationalNumber; // Get the local part of the number

  return {
    dialCode,
    localNumber,
  };
  if (parsedPhoneNumber?.isValid()) {
  }

  return null; // Return null if the phone number is invalid
};

export function timeAgo(minutes) {
  if (minutes < 1) return "just now";

  const mins = minutes;
  const hours = minutes / 60;
  const days = minutes / 1440; // 60 * 24

  if (mins < 60) {
    return `${Math.floor(mins)} min${mins >= 2 ? "s" : ""} ago`;
  }

  if (hours < 24) {
    return `${Math.floor(hours)} hr${hours >= 2 ? "s" : ""} ago`;
  }

  return `${Math.floor(days)} day${days >= 2 ? "s" : ""} ago`;
}

export function playNotificationSound() {
  const audio = new Audio("/notification.mp3");
  audio.play().catch((err) => {
    console.log("Audio play failed:", err);
  });
}

function formatPhoneNumber(phoneNumber) {
  // Parse the phone number with the provided country code (e.g., 'GB' for UK)
  const phone = parsePhoneNumberFromString(phoneNumber.toString());

  // Check if the phone number is valid
  if (!phone) {
    return "Invalid phone number.";
  }

  // Format the phone number in international format
  return phone.formatInternational();
}

const isNullOrEmpty = (value) => {
  return (
    !value ||
    value === undefined ||
    value === "" ||
    value.length === 0 ||
    value === "undefined" ||
    value === "null" ||
    value === "Invalid Date"
  );
};

const getValueOrPlaceholder = (value) => {
  if (
    value === null ||
    value === undefined ||
    (typeof value === "string" && value.trim() === "") ||
    (Array.isArray(value) && value.length === 0)
  ) {
    return "-";
  }

  return value;
};

export const timeSincePost = (postedDateTime) => {
  const now = dayjs();
  const postedTime = dayjs(postedDateTime);
  const timeDiff = now.diff(postedTime);

  if (timeDiff < 60 * 1000) {
    // Less than a minute
    const seconds = Math.round(timeDiff / 1000);
    return seconds > 5 ? `${seconds} seconds ago` : "Just now";
  } else if (timeDiff < 60 * 60 * 1000) {
    // Less than an hour
    const minutes = Math.round(timeDiff / (60 * 1000));
    return `${minutes} ${minutes > 1 ? "minutes ago" : "minute ago"}`;
  } else if (timeDiff < 24 * 60 * 60 * 1000) {
    // Less than a day
    const hours = Math.round(timeDiff / (60 * 60 * 1000));
    return `${hours} ${hours > 1 ? "hours ago" : "hour ago"}`;
  } else if (timeDiff < 7 * 24 * 60 * 60 * 1000) {
    // Less than a week
    const days = Math.round(timeDiff / (24 * 60 * 60 * 1000));
    return `${days} ${days > 1 ? "days ago" : "day ago"}`;
  } else if (timeDiff < 4 * 7 * 24 * 60 * 60 * 1000) {
    // Less than a month
    const weeks = Math.round(timeDiff / (7 * 24 * 60 * 60 * 1000));
    return `${weeks} ${weeks > 1 ? "weeks ago" : "week ago"}`;
  } else if (timeDiff < 12 * 4 * 7 * 24 * 60 * 60 * 1000) {
    // Less than a year
    const months = Math.round(timeDiff / (4 * 7 * 24 * 60 * 60 * 1000));
    return `${months} ${months > 1 ? "months ago" : "month ago"}`;
  } else {
    // A year or more
    const years = Math.round(timeDiff / (12 * 4 * 7 * 24 * 60 * 60 * 1000));
    return `${years} ${years > 1 ? "years ago" : "year ago"}`;
  }
};

function hoursToYears(hours, useLeapYear = false) {
  const hoursPerYear = useLeapYear ? 8766 : 8760; // 8760 for regular year, 8766 for leap year adjustment
  return hours / hoursPerYear;
}

function formatDate(inputDate) {
  const date = new Date(inputDate);
  const day = String(date?.getDate()).padStart(2, "0");
  const month = date?.toLocaleString("default", { month: "short" });
  const year = String(date?.getFullYear()).slice(-2);
  const hour = String(date?.getHours()).padStart(2, "0");
  const minute = String(date?.getMinutes()).padStart(2, "0");
  return `${day} ${month}-${year} ${hour}:${minute}`;
}

function getJobStatusLabel(status) {
  const entry = Object.entries(JobStatus).find(
    ([_, value]) => value === status
  );
  return entry?.[0]; // Returns the key like "DRAFT", "LIVE", etc.
}

// function isObjectEmpty(obj: Record<string, any>): boolean {
//   return obj && Object.keys(obj).length === 0 && obj.constructor === Object;
// }
function isObjectEmpty(obj) {
  return obj
    ? Object.keys(obj).length === 0 && obj.constructor === Object
    : true;
}

function isDeepEqual(data1, data2) {
  return JSON.stringify(data1) === JSON.stringify(data2);
}

export function getQueryParams() {
  const params = new URLSearchParams(window.location.search);
  const obj = {};
  for (const [key, value] of params.entries()) {
    obj[key] = value;
  }
  return obj;
}

function getEnumKeyFromValue(obj, value) {
  return Object.keys(obj).find(key => obj[key] === value);
}


export {
  extractDialCode,
  formatPhoneNumber,
  isNullOrEmpty,
  getValueOrPlaceholder,
  hoursToYears,
  formatDate,
  getJobStatusLabel,
  isObjectEmpty,
  isDeepEqual,
  getEnumKeyFromValue
};
